import React, { useEffect, useState } from 'react'
import Dropzone from 'react-dropzone'
import { Box, Button, Typography, TextField, MenuItem } from '@material-ui/core'
import { Pagination } from '@material-ui/lab'
import {
    CloudUpload as UploadIcon,
    CloudDownload as DownloadIcon,
} from '@material-ui/icons'

import { useRentRoll } from '../../hooks/useRentRoll'
import { useLeaseAuditDetail } from '../../hooks/useLeaseAuditDetail'
import { RentRollTrackProgress } from './RentRollTrackProgress'
import { RentRollTableHeader } from './RentRollTableHeader'
import { RentRollTable } from './RentRollTable'
import { RentRollReview } from './RentRollReview'
import { RentRollAnalyzing } from './RentRollAnalyzing'
import { useRentRollRootStyles } from './styles'
import { HEADER_HEIGHT, COLLAPSABLE_SELECTOR_HEIGHT } from '../constants'

interface RentRollTabProps {
    auditId: number
    rentRollProcessingTaskId?: string | null
    isCollapsableSelectorOpen: boolean
}

export const RentRollRoot: React.FC<RentRollTabProps> = ({
    auditId,
    rentRollProcessingTaskId,
    isCollapsableSelectorOpen,
}) => {
    const headerHeight = HEADER_HEIGHT
    const collapsableSelectorHeight = isCollapsableSelectorOpen
        ? COLLAPSABLE_SELECTOR_HEIGHT
        : 0
    const classes = useRentRollRootStyles({
        headerHeight,
        collapsableSelectorHeight,
    })
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(50)
    const [search, setSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState(search)
    const [sortColumn, setSortColumn] = useState<string | null>(null)
    const [activeTaskId, setActiveTaskId] = useState<string | null>(
        rentRollProcessingTaskId ?? null,
    )

    const { leaseAudit } = useLeaseAuditDetail({ auditId })
    const isUploadedAudit = leaseAudit?.audit_type === 'UPLOADED'

    const {
        rows,
        attributes: unsortedAttributes,
        totalCount,
        totalPages,
        isFetchingRentRoll,
        isDeletingRentRoll,
        isDownloadingRentRoll,
        isUploadingRentRoll,
        isFetchingIntegratedRentRoll,
        rentRollTaskProgress,
        isLoadingTaskProgress,
        rentRollTaskProgressError,
        uploadTaskHasFailed,
        uploadTaskFailureMessage,
        rentRollSummary,
        hasUploadedFile,
        isLoadingSummary,
        isConfirmingParse,
        isReportingIssue,
        isDetecting,
        isParsing,
        deleteRentRoll,
        downloadRentRoll,
        uploadRentRoll,
        fetchIntegratedRentRoll,
        confirmParse,
        reportIssue,
        refetchSummary,
    } = useRentRoll({
        auditId,
        page,
        pageSize,
        search: debouncedSearch,
        sortColumn,
        progressTaskId: activeTaskId,
        summaryEnabled: isUploadedAudit,
    })

    const attributes = [...unsortedAttributes].sort(
        (a, b) => a.display_order - b.display_order,
    )

    useEffect(() => {
        if (rentRollProcessingTaskId) {
            setActiveTaskId(rentRollProcessingTaskId)
        }
    }, [rentRollProcessingTaskId])

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setDebouncedSearch(search)
        }, 400)

        return () => {
            window.clearTimeout(timeoutId)
        }
    }, [search])

    useEffect(() => {
        setPage(1)
    }, [debouncedSearch, pageSize])

    useEffect(() => {
        if (!activeTaskId) {
            return
        }

        if (rentRollTaskProgress?.task_status === 'FAILED') {
            setActiveTaskId(null)
            void refetchSummary()
            return
        }

        if (rentRollTaskProgress?.task_status !== 'SUCCESS') {
            return
        }

        const hadParsingSubTask = rentRollTaskProgress.sub_tasks?.some(
            (subTask) => subTask.sub_task_type === 'CURRENT_TERMS_PROCESSING',
        )

        setActiveTaskId(null)

        if (!hadParsingSubTask) {
            void refetchSummary()
        }
    }, [activeTaskId, rentRollTaskProgress, refetchSummary])

    useEffect(() => {
        if (
            activeTaskId &&
            rentRollTaskProgressError?.response?.status === 404 &&
            rentRollSummary?.parse_config_status === 'DETECTED'
        ) {
            setActiveTaskId(null)
        }
    }, [
        activeTaskId,
        rentRollTaskProgressError,
        rentRollSummary?.parse_config_status,
    ])

    const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
        setPage(value)
    }

    const handleFileDrop = (acceptedFiles: File[]) => {
        if (acceptedFiles.length > 0) {
            uploadRentRoll(acceptedFiles[0], {
                onSuccess: (response) => {
                    setActiveTaskId(response.data.task_id)
                },
            })
        }
    }

    const handleConfirmParse = async () => {
        const response = await confirmParse()
        setActiveTaskId(response.data.task_id)
    }

    const handleUploadDifferentFile = () => {
        deleteRentRoll(undefined, {
            onSuccess: () => {
                setActiveTaskId(null)
            },
        })
    }

    const renderDropzone = (disabled = false) => (
        <Box className={classes.uploadSection}>
            <Dropzone
                onDrop={handleFileDrop}
                accept={{
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
                        '.xlsx',
                    ],
                    'text/csv': ['.csv'],
                }}
                multiple={false}
                disabled={disabled || isUploadingRentRoll}
            >
                {({ getRootProps, getInputProps, isDragActive }) => (
                    <div
                        {...getRootProps()}
                        className={`${classes.dropzone} ${
                            isDragActive ? classes.dropzoneActive : ''
                        }`}
                    >
                        <input {...getInputProps()} />
                        <UploadIcon
                            style={{ fontSize: 32, marginBottom: 8 }}
                            color="action"
                        />
                        <Typography variant="body2" color="textSecondary">
                            {isUploadingRentRoll
                                ? 'Uploading...'
                                : 'Drop a rent roll file here, or click to select'}
                        </Typography>
                    </div>
                )}
            </Dropzone>
        </Box>
    )

    const showReview =
        isUploadedAudit &&
        rows.length === 0 &&
        !activeTaskId &&
        !isDetecting &&
        !isParsing &&
        rentRollSummary &&
        (rentRollSummary.parse_config_status === 'DETECTED' ||
            rentRollSummary.parse_config_status === 'FAILED' ||
            rentRollSummary.parse_config_status === 'MANUAL')

    const showAnalyzing =
        isUploadedAudit &&
        rows.length === 0 &&
        (isDetecting || (Boolean(activeTaskId) && isLoadingTaskProgress))

    const showParsing =
        isUploadedAudit &&
        rows.length === 0 &&
        isParsing &&
        !uploadTaskHasFailed

    const showUploadEmptyState =
        isUploadedAudit &&
        leaseAudit?.current_step === 'RENT_ROLL_PROCESSING' &&
        rows.length === 0 &&
        !showAnalyzing &&
        !showReview &&
        !showParsing

    if (
        showParsing ||
        (activeTaskId && uploadTaskHasFailed && rows.length === 0)
    ) {
        return (
            <Box className={classes.container}>
                <RentRollTrackProgress
                    taskProgress={rentRollTaskProgress}
                    isLoading={isLoadingTaskProgress}
                    error={rentRollTaskProgressError}
                    hasFailed={uploadTaskHasFailed}
                    failureMessage={uploadTaskFailureMessage}
                />
                {uploadTaskHasFailed && rentRollSummary && (
                    <Box mt={2}>
                        <RentRollReview
                            summary={rentRollSummary}
                            isFailure
                            failureMessage={uploadTaskFailureMessage}
                            isReportingIssue={isReportingIssue}
                            isDeleting={isDeletingRentRoll}
                            onConfirm={handleConfirmParse}
                            onReportIssue={reportIssue}
                            onUploadDifferentFile={handleUploadDifferentFile}
                        />
                    </Box>
                )}
            </Box>
        )
    }

    if (showAnalyzing) {
        return (
            <Box className={classes.container}>
                <RentRollAnalyzing filename={rentRollSummary?.filename} />
            </Box>
        )
    }

    if (showReview) {
        return (
            <Box className={classes.container}>
                <RentRollReview
                    summary={rentRollSummary}
                    isFailure={rentRollSummary.parse_config_status === 'FAILED'}
                    failureMessage={
                        rentRollSummary.notes ??
                        'We could not analyze this rent roll automatically.'
                    }
                    isConfirming={isConfirmingParse}
                    isReportingIssue={isReportingIssue}
                    isDeleting={isDeletingRentRoll}
                    onConfirm={handleConfirmParse}
                    onReportIssue={reportIssue}
                    onUploadDifferentFile={handleUploadDifferentFile}
                />
            </Box>
        )
    }

    if (showUploadEmptyState) {
        return (
            <Box className={classes.container}>
                {renderDropzone()}
                <Box className={classes.emptyState}>
                    <Typography variant="h6" gutterBottom>
                        Upload your rent roll
                    </Typography>
                    <Typography variant="body2" align="center">
                        Drop your Excel rent roll here. We will analyze it and
                        show you how many residents we found before processing.
                    </Typography>
                </Box>
            </Box>
        )
    }

    if (
        rows.length === 0 &&
        leaseAudit?.audit_type === 'INTEGRATED' &&
        leaseAudit?.current_step === 'RENT_ROLL_PROCESSING'
    ) {
        return (
            <Box className={classes.container}>
                <Box className={classes.emptyState}>
                    <Box className={classes.emptyStateCard}>
                        <DownloadIcon className={classes.emptyStateIcon} />
                        <Typography variant="h6">
                            Rent roll data not loaded
                        </Typography>
                        <Typography variant="body2" align="center">
                            Pull the latest integrated rent roll to populate
                            this tab and start reviewing resident data.
                        </Typography>
                        <Box mt={1}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => fetchIntegratedRentRoll()}
                                disabled={isFetchingIntegratedRentRoll}
                            >
                                {isFetchingIntegratedRentRoll
                                    ? 'Getting rent roll data...'
                                    : 'Get rent roll data'}
                            </Button>
                        </Box>
                        <Typography variant="caption" color="textSecondary">
                            This may take a minute.
                        </Typography>
                    </Box>
                </Box>
            </Box>
        )
    }

    if (
        rows.length === 0 &&
        isUploadedAudit &&
        hasUploadedFile &&
        isLoadingSummary
    ) {
        return (
            <Box className={classes.container}>
                <RentRollAnalyzing filename={rentRollSummary?.filename} />
            </Box>
        )
    }

    return (
        <Box className={classes.container}>
            <RentRollTableHeader
                search={search}
                onSearchChange={setSearch}
                onDownload={
                    isUploadedAudit
                        ? () =>
                              downloadRentRoll(
                                  rentRollSummary?.filename ?? undefined,
                              )
                        : undefined
                }
                isDownloadDisabled={!hasUploadedFile}
                isDownloading={isDownloadingRentRoll}
                onDelete={deleteRentRoll}
                isDeleteDisabled={isDeletingRentRoll}
                classes={classes}
            />

            <RentRollTable
                rows={rows}
                attributes={attributes}
                sortColumn={sortColumn}
                setSortColumn={setSortColumn}
                isLoading={isFetchingRentRoll}
                classes={classes}
            />

            {totalPages > 1 && (
                <Box className={classes.pagination}>
                    <TextField
                        select
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                        variant="outlined"
                        size="small"
                        style={{ width: 80 }}
                    >
                        {[25, 50, 100].map((size) => (
                            <MenuItem key={size} value={size}>
                                {size}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={handlePageChange}
                        color="primary"
                    />
                    <Typography variant="body2" color="textSecondary">
                        {totalCount > 0 ? (page - 1) * pageSize + 1 : 0} -{' '}
                        {Math.min(page * pageSize, totalCount)} of {totalCount}
                    </Typography>
                </Box>
            )}
        </Box>
    )
}
